import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Loader2, Copy, Check, Lightbulb, GraduationCap, PenLine, Code, Briefcase, Rocket, Image as ImageIcon, ArrowLeft,
} from "lucide-react";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { PageTransition } from "@/components/PageTransition";
import { ProBadge } from "@/components/AskAI";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const ICONS = {
  "graduation-cap": GraduationCap, "pen-line": PenLine, code: Code,
  briefcase: Briefcase, rocket: Rocket, image: ImageIcon,
};
const COLOR = {
  lime: "bg-brand-lime text-brand-ink", cyber: "bg-brand-cyber text-white",
  pink: "bg-brand-pink text-white", tangerine: "bg-brand-tangerine text-brand-ink",
};

function PromptCard({ p }) {
  const [copied, setCopied] = useState(false);
  const markCopied = () => {
    setCopied(true);
    toast.success("Prompt copied — paste it into your AI tool");
    setTimeout(() => setCopied(false), 1600);
  };
  const copy = () => {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(p.prompt).then(markCopied).catch(fallbackCopy);
    } else {
      fallbackCopy();
    }
  };
  const fallbackCopy = () => {
    try {
      const ta = document.createElement("textarea");
      ta.value = p.prompt;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
      markCopied();
    } catch {
      toast.error("Couldn't copy — select the text and copy manually.");
    }
  };
  return (
    <div className="rounded-2xl border border-border bg-card shadow-brutal-sm p-5 flex flex-col" data-testid="prompt-card">
      <div className="flex items-start justify-between gap-2">
        <h4 className="font-heading font-bold tracking-tight">{p.title}</h4>
        <button onClick={copy} data-testid="copy-prompt"
          className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent brutal-press">
          {copied ? <><Check className="h-3.5 w-3.5 text-success" /> Copied</> : <><Copy className="h-3.5 w-3.5" /> Copy</>}
        </button>
      </div>
      <p className="mt-3 text-sm text-muted-foreground leading-relaxed bg-secondary/60 rounded-xl p-3 font-mono">{p.prompt}</p>
      {p.tip && (
        <p className="mt-3 text-xs flex items-start gap-1.5 text-foreground/70">
          <Lightbulb className="h-3.5 w-3.5 text-brand-tangerine shrink-0 mt-0.5" /> {p.tip}
        </p>
      )}
    </div>
  );
}

export default function PromptLibrary() {
  const navigate = useNavigate();
  const [packs, setPacks] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/pro/prompts").then(({ data }) => setPacks(data.packs || [])).catch(() => setPacks([]));
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <PageTransition className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-20">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>

        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-mono text-xs font-bold uppercase tracking-widest text-primary bg-primary/10 rounded-full px-3 py-1.5">Prompt library</span>
          <ProBadge />
        </div>
        <h1 className="mt-4 font-heading font-extrabold tracking-tight text-3xl md:text-4xl">Copy-paste prompts that actually work</h1>
        <p className="mt-3 text-muted-foreground max-w-2xl">Battle-tested prompt templates for learning, writing, coding, careers and more. Tap copy, drop in the blanks, paste into any AI tool.</p>

        {!packs ? (
          <div className="mt-16 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="mt-10 space-y-12">
            {packs.map((pack, pi) => {
              const Icon = ICONS[pack.icon] || Lightbulb;
              return (
                <motion.section key={pack.slug} data-testid={`prompt-pack-${pack.slug}`}
                  initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: Math.min(pi * 0.05, 0.2) }}>
                  <div className="flex items-center gap-3 mb-5">
                    <span className={`grid place-items-center h-11 w-11 rounded-xl border border-border ${COLOR[pack.color] || "bg-secondary"}`}><Icon className="h-5 w-5" /></span>
                    <div>
                      <h2 className="font-heading font-extrabold tracking-tight text-xl md:text-2xl">{pack.title}</h2>
                      <p className="text-sm text-muted-foreground">{pack.blurb}</p>
                    </div>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    {pack.prompts.map((p, i) => <PromptCard key={i} p={p} />)}
                  </div>
                </motion.section>
              );
            })}
          </div>
        )}
      </PageTransition>
    </div>
  );
}
